import React, { ChangeEvent, useRef, useState } from 'react';
import { HeaderPage } from '../../../components/HeaderPage/HeaderPage';
import { DASHBOARD_ROUTES } from '../../../Routes';
import { useDispatch, useSelector } from 'react-redux';
import { saveCompany, selectCompany } from '../../../store/companySlide';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CompanyInfo } from '../../../data/models/company.model';
import { toast } from 'react-toastify';
import {
  ImportMemberEventResult,
  isValidImportMemberEventResult,
  uploadEmployeesCSV,
  uploadMemberEventCSV,
} from '../../../data/api/company-employee.api';
import { getCompanyDetail } from '../../../data/api/company.api';
import {
  Box,
  CircularProgress,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Button from '../../../components/Button';
import ModalCustom from '../../../components/ModalCustom';

const ACCEPT_TYPES = ['.csv', 'text/csv'];

const MEMBERS_ONLY_TEMPLATE_HEADERS = [
  'Index',
  'First Name',
  'Last Name',
  'Primary Email',
  'Primary Email Type(Work/Personal)',
  'Secondary Email',
  'Secondary Email Type(Work/Personal)',
  'Phone (1234567890 or (xxx) xxx-xxxx)',
  'Employment Status (ACTIVE/PENDING/INACTIVE)',
  'Address 1',
  'Address 2',
  'City',
  'Zip Code (5 digit)',
  'State Abbreviations',
  'Is Admin (TRUE/FALSE)',
  'Is Send Welcome Email (TRUE/FALSE)',
  'Is Employee (TRUE/FALSE)',
];

const MEMBER_EVENT_TEMPLATE_HEADERS = [
  'Index',
  'Event Type',
  'Branch Location',
  'External ID',
  'Member First Name',
  'Member Last Name',
  'Primary Email',
  'Primary Email Type (Work/Personal)',
  'Phone',
  'Address 1',
  'Address 2',
  'City',
  'State Abbreviations',
  'Zip Code (5 digit)',
  'Is Admin (TRUE/FALSE)',
  'Is Employee (TRUE/FALSE)',
  'Is Active (TRUE/FALSE)',
  'Patient Name',
  'Relationship',
  'Disease',
  'Death Date',
  'Risk Level',
  'Internal Notes',
];

// Row keys returned by the backend, aligned to MEMBER_EVENT_TEMPLATE_HEADERS.
const MEMBER_EVENT_ROW_KEYS = [
  'index',
  'eventType',
  'branchLocation',
  'externalId',
  'firstName',
  'lastName',
  'primaryEmail',
  'primaryEmailType',
  'phone',
  'address1',
  'address2',
  'city',
  'state',
  'zipCode',
  'isAdmin',
  'isEmployee',
  'isActive',
  'patientName',
  'relationship',
  'disease',
  'deathDate',
  'riskLevel',
  'internalNotes',
];

const CSV_INJECTION_PREFIXES = ['=', '+', '-', '@', '\t', '\r'];

const errorToMessage = (error: unknown): string => {
  if (typeof error === 'string') return error;
  if (Array.isArray(error)) return error.join('; ');
  if (error instanceof Error) return error.message;
  return '';
};

const escapeCsvCell = (value: unknown): string => {
  let cell = String(value ?? '');
  if (cell.length > 0 && CSV_INJECTION_PREFIXES.includes(cell[0])) {
    cell = `'${cell}`;
  }
  return `"${cell.replace(/"/g, '""')}"`;
};

const downloadCsv = (csv: string, filename: string) => {
  const data = new Blob([csv], { type: 'text/csv' });
  const csvURL = window.URL.createObjectURL(data);
  const tempLink = document.createElement('a');
  tempLink.href = csvURL;
  tempLink.setAttribute('download', filename);
  tempLink.click();
};

const buildErrorReportCsv = (result: ImportMemberEventResult): string => {
  const headerLine = [
    'Row Number',
    ...MEMBER_EVENT_TEMPLATE_HEADERS,
    'Status',
    'Errors',
    'Warnings',
  ]
    .map(escapeCsvCell)
    .join(',');

  const lines = result.rows
    .filter((row) => row.errors.length > 0 || row.warnings.length > 0)
    .map((row) => {
      const cells = [
        String(row.rowNumber),
        ...MEMBER_EVENT_ROW_KEYS.map((key) => row.row[key] || ''),
        row.status,
        row.errors.join('; '),
        row.warnings.join('; '),
      ];
      return cells.map(escapeCsvCell).join(',');
    });

  return [headerLine, ...lines].join('\n');
};

export const CompanyImportUser = () => {
  const { company_id } = useParams();
  const companyInfo: CompanyInfo = useSelector(selectCompany);
  const [isUploading, setIsUploading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [importResult, setImportResult] =
    useState<ImportMemberEventResult | null>(null);
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const membersFileRef = useRef<HTMLInputElement>(null);
  const memberEventFileRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchCompanyDetail = async (companyId: string) => {
    await getCompanyDetail(companyId)
      .then((resp) => {
        dispatch(saveCompany(resp.company));
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const openFilePicker = (ref: React.RefObject<HTMLInputElement>) => {
    setAnchorEl(null);
    if (ref.current) {
      ref.current.value = '';
      ref.current.click();
    }
  };

  const handleUploadEmployeesCSV = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (isUploading || !files?.length || !company_id) {
      return;
    }
    const companyId = company_id;
    const fileCurrent = files[0];

    const isCsvFile =
      fileCurrent.name.toLowerCase().endsWith('.csv') ||
      ACCEPT_TYPES.includes(fileCurrent.type);
    if (!isCsvFile) {
      toast.error(
        'This file type is not supported. The following file types are supported: .csv!',
      );
      return;
    }

    setIsUploading(true);
    void uploadEmployeesCSV(companyId, fileCurrent)
      .then((resp) => {
        if (!resp.success) {
          toast.error(resp.message);
          return;
        }
        toast.success('Upload CSV success!');
        void fetchCompanyDetail(companyId);
        navigate(DASHBOARD_ROUTES.COMPANY_ID_EMPLOYEES(companyId));
      })
      .catch((error) => {
        toast.error(errorToMessage(error) || 'Upload CSV fail!');
        console.error(error);
      })
      .finally(() => {
        setIsUploading(false);
      });
  };

  const handleUploadMemberEventCSV = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (isUploading || !files?.length || !company_id) {
      return;
    }
    const companyId = company_id;
    const fileCurrent = files[0];

    const isCsvFile =
      fileCurrent.name.toLowerCase().endsWith('.csv') ||
      ACCEPT_TYPES.includes(fileCurrent.type);
    if (!isCsvFile) {
      toast.error(
        'This file type is not supported. The following file types are supported: .csv!',
      );
      return;
    }

    setIsUploading(true);
    void uploadMemberEventCSV(companyId, fileCurrent)
      .then((resp) => {
        const result = resp?.data;
        if (!isValidImportMemberEventResult(result)) {
          toast.error(resp?.message || 'Import failed');
          return;
        }
        setImportResult(result);
        setResultModalOpen(true);
      })
      .catch((error) => {
        toast.error(errorToMessage(error) || 'Import failed');
        console.error(error);
      })
      .finally(() => {
        setIsUploading(false);
      });
  };

  const handleDownloadMembersTemplate = () => {
    setAnchorEl(null);
    downloadCsv(
      MEMBERS_ONLY_TEMPLATE_HEADERS.join(','),
      'employees_template.csv',
    );
  };

  const handleDownloadMemberEventTemplate = () => {
    setAnchorEl(null);
    downloadCsv(
      MEMBER_EVENT_TEMPLATE_HEADERS.join(','),
      'MemberEvent_import_template.csv',
    );
  };

  const handleDownloadErrorReport = () => {
    if (!importResult) {
      return;
    }
    downloadCsv(
      buildErrorReportCsv(importResult),
      'MemberEvent_import_report.csv',
    );
  };

  const handleResultModalClose = (_event?: unknown, reason?: string) => {
    const flagged = importResult
      ? importResult.summary.rowsErrored + importResult.summary.rowsWarned > 0
      : false;
    if (reason && flagged) {
      return; // keep diagnostics up until explicitly closed
    }
    const result = importResult;
    setResultModalOpen(false);
    setImportResult(null);
    if (
      result &&
      company_id &&
      (result.summary.membersCreated > 0 || result.summary.eventsCreated > 0)
    ) {
      void fetchCompanyDetail(company_id);
      navigate(DASHBOARD_ROUTES.COMPANY_ID_EMPLOYEES(company_id));
    }
  };

  const summary = importResult?.summary;
  const hasErroredOrWarned = summary
    ? summary.rowsErrored + summary.rowsWarned > 0
    : false;

  return (
    <>
      <HeaderPage
        title={{
          title: companyInfo?.name ? companyInfo?.name : '',
          hasBack: true,
        }}
        tabs={[
          {
            title: 'Overview',
            selected: false,
            link: DASHBOARD_ROUTES.COMPANY_DETAIL + company_id,
          },
          {
            title: 'Company Notes',
            selected: false,
            link: DASHBOARD_ROUTES.COMPANY_ID_NOTES(company_id),
          },
          {
            title: 'Users',
            selected: true,
            link: DASHBOARD_ROUTES.COMPANY_ID_EMPLOYEES(company_id),
          },
        ]}
      />

      <div className="nv-container">
        <div className="section employees">
          <div className="tabs bg-[#FDFAF7] w-[360px]">
            <Stack
              direction={'row'}
              justifyContent={'space-around'}
              height={'49px'}
              alignItems={'center'}
            >
              <Link
                to={DASHBOARD_ROUTES.COMPANY_ID_EMPLOYEES(company_id)}
                className="tab"
                style={{
                  color: '#213B54',
                  fontSize: '13px',
                  fontWeight: 700,
                  textDecoration: 'none',
                }}
              >
                Users
              </Link>
              <Link
                to={DASHBOARD_ROUTES.COMPANY_EMPLOYEES_ADD(company_id)}
                className="tab"
                style={{
                  color: '#213B54',
                  fontSize: '13px',
                  fontWeight: 700,
                  textDecoration: 'none',
                }}
              >
                Add a User
              </Link>
              <Link
                to={DASHBOARD_ROUTES.COMPANY_EMPLOYEES_IMPORT(company_id)}
                className="tab "
                style={{
                  color: '#213B54',
                  height: '100%',
                  alignItems: 'center',
                  borderBottom: '3px solid #213B54',
                  textAlign: 'center',
                  display: 'flex',
                  fontSize: '13px',
                  textDecoration: 'none',
                  fontWeight: 700,
                }}
              >
                Import Users
              </Link>
            </Stack>
          </div>
          <hr />

          <div className="filter no-padding" />
        </div>
      </div>

      <div className="nv-container">
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'start',
          }}
        >
          <Typography
            sx={{
              fontSize: '24px',
              color: 'black',
              fontWeight: 700,
              marginY: '24px',
            }}
          >
            CSV Import
          </Typography>
          <Typography
            sx={{
              color: '#adadad',
              marginBottom: '8px',
            }}
          >
            Bulk import using CSV
          </Typography>
          <Typography
            sx={{
              color: 'black',
              marginBottom: '8px',
            }}
          >
            The uploaded CSV file must contain a row of headers signifying{' '}
            <strong>first name</strong>, <strong>last name</strong>,
            <strong> primary email</strong>, <strong>primary email type</strong>
            , and <strong>phone</strong>.
          </Typography>
          <Typography
            sx={{
              color: 'black',
              marginBottom: '8px',
            }}
          >
            The columns for <strong>state</strong> and <strong>address</strong>{' '}
            are optional.
          </Typography>
          <Typography
            sx={{
              color: 'black',
            }}
          >
            Use <strong>Import Member &amp; Event</strong> to import members
            together with their loss events. Download a template to see the
            expected columns for either import.
          </Typography>
          <div className="mt-[30px]">
            <Button
              variant="contained"
              color="primary"
              disabled={isUploading}
              onClick={(event) => setAnchorEl(event.currentTarget)}
              endIcon={
                isUploading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <KeyboardArrowDownIcon />
                )
              }
              sx={{
                fontWeight: 700,
                fontSize: '14px',
                paddingX: '24px',
              }}
            >
              {isUploading ? 'Importing…' : 'Import'}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { marginTop: '4px', minWidth: '260px' } }}
            >
              <MenuItem onClick={() => openFilePicker(membersFileRef)}>
                Import members only
              </MenuItem>
              <MenuItem onClick={handleDownloadMembersTemplate}>
                Download member only template
              </MenuItem>
              <MenuItem onClick={() => openFilePicker(memberEventFileRef)}>
                Import Member &amp; Event
              </MenuItem>
              <MenuItem onClick={handleDownloadMemberEventTemplate}>
                Download Member &amp; Event template
              </MenuItem>
            </Menu>
            <input
              ref={membersFileRef}
              type="file"
              accept=".csv, text/csv"
              hidden
              onChange={handleUploadEmployeesCSV}
            />
            <input
              ref={memberEventFileRef}
              type="file"
              accept=".csv, text/csv"
              hidden
              onChange={handleUploadMemberEventCSV}
            />
          </div>
        </Box>
      </div>

      <ModalCustom
        modal={resultModalOpen}
        handleClose={handleResultModalClose}
        title={
          summary && summary.rowsErrored > 0
            ? 'Import completed with errors'
            : 'Import complete'
        }
        content={
          summary ? (
            <Stack
              sx={{
                width: { xs: 'auto', md: '480px' },
                paddingX: '24px',
                paddingBottom: '8px',
              }}
              spacing={1}
            >
              <Typography>Rows processed: {summary.rowsProcessed}</Typography>
              <Typography>Members created: {summary.membersCreated}</Typography>
              <Typography>Members matched: {summary.membersMatched}</Typography>
              <Typography>Events created: {summary.eventsCreated}</Typography>
              {summary.rowsErrored > 0 && (
                <Typography>Rows with errors: {summary.rowsErrored}</Typography>
              )}
              {summary.rowsWarned > 0 && (
                <Typography>
                  Rows with warnings: {summary.rowsWarned}
                </Typography>
              )}
            </Stack>
          ) : null
        }
        groupButton={
          <Stack
            direction={'row'}
            sx={{
              paddingBottom: '24px',
              paddingRight: '24px',
              justifyContent: 'end',
            }}
            spacing={'12px'}
          >
            {hasErroredOrWarned && (
              <Button
                variant="outlined"
                onClick={handleDownloadErrorReport}
                sx={{
                  fontWeight: 700,
                  fontSize: '14px',
                  height: '40px',
                  lineHeight: '20px',
                  border: '1px solid #213B54',
                  color: '#213B54',
                }}
              >
                Download error report
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleResultModalClose}
              sx={{
                fontWeight: 700,
                fontSize: '14px',
                width: '97px',
                height: '40px',
                lineHeight: '20px',
              }}
            >
              Close
            </Button>
          </Stack>
        }
      />
    </>
  );
};
